import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  NotificationsActive as AlertIcon,
  Send as SendIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { DashboardStats } from '../types/client';
import { ClientService } from '../services/clientService';

const CRMDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentNotifications, setRecentNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsData, notifData] = await Promise.all([
        ClientService.getDashboardStats(),
        ClientService.getRecentNotifications(20),
      ]);
      setStats(statsData);
      setRecentNotifications(notifData);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return 'success';
      case 'email': return 'primary';
      case 'sms': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'skipped': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const maxSectorCount = stats?.top_sectors?.[0]?.client_count || 1;

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <PeopleIcon color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.total_clients || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Clients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <AlertIcon color="success" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.active_alerts || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Active Alerts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SendIcon color="warning" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.docs_sent_this_week || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Sent This Week
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingIcon color="secondary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.top_sectors?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Active Sectors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Top Sectors */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Sectors by Interest
              </Typography>
              {stats?.top_sectors && stats.top_sectors.length > 0 ? (
                <Box>
                  {stats.top_sectors.map((sector) => (
                    <Box key={sector.name} sx={{ mb: 1.5 }}>
                      <Box display="flex" justifyContent="space-between" mb={0.25}>
                        <Typography variant="body2">{sector.name}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {sector.client_count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(sector.client_count / maxSectorCount) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No sector interests yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Notifications */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Notifications
              </Typography>
              {recentNotifications.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentNotifications.map((notif) => (
                        <TableRow key={notif.id}>
                          <TableCell>
                            {notif.client?.first_name} {notif.client?.last_name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notif.channel}
                              size="small"
                              color={getChannelColor(notif.channel) as any}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notif.status}
                              size="small"
                              color={getStatusColor(notif.status) as any}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatTime(notif.created_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No notifications sent yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CRMDashboard;
